import { Card, CardContent } from '@/components/ui/card'
import type { GHLQueueStatus } from '@/lib/ghl-dashboard'
import { Activity, Clock, Layers, Database } from 'lucide-react'

interface Props {
  readonly status: GHLQueueStatus | null | undefined
  readonly isLoading: boolean
  readonly isError: boolean
}

function Indicator({ value, max, label, icon: Icon }: { value: number; max?: number; label: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <div>
        <p className="text-sm font-semibold">
          {value}{max !== undefined && <span className="text-muted-foreground font-normal">/{max}</span>}
        </p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  )
}

export function QueueStatusWidget({ status, isLoading, isError }: Props) {
  if (isError || (!isLoading && !status)) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-2 w-2 rounded-full bg-gray-400" />
            <span>Queue status unavailable</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading || !status) {
    return (
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground animate-pulse">
            <div className="h-2 w-2 rounded-full bg-yellow-400" />
            <span>Connecting...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  const utilizationPct = (status.active / status.maxConcurrent) * 100
  const isHealthy = status.queued === 0 && utilizationPct < 70
  const isWarning = status.queued > 0 || utilizationPct >= 70
  const isCritical = status.queued > 5 || utilizationPct >= 100

  const dotColor = isCritical ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-green-500'

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`h-2 w-2 rounded-full ${dotColor} animate-pulse`} />
            <span className="text-sm font-medium">
              {isCritical ? 'At Capacity' : isWarning ? 'Busy' : 'Healthy'}
            </span>
          </div>

          <div className="flex items-center gap-6">
            <Indicator value={status.active} max={status.maxConcurrent} label="Active" icon={Activity} />
            <Indicator value={status.queued} label="Queued" icon={Layers} />
            <Indicator value={status.pendingTimers} label="Pending" icon={Clock} />
            <Indicator value={status.cooldownCacheSize} label="Cached" icon={Database} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
